import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import {
  PortfolioInvestmentCardContainer,
  LeftSectionContainer,
  RightSectionContainer,
  TopLeftSectionContainer,
  CurrentValueContainer,
  GrowthContainer,
  DealLogo,
  AssetClassContainer,
  AssetClassText,
  HeadingWrapper,
  TagsWrapper,
} from "./styled";
import { ThemedText } from "@components/ThemedText";
import useCurrency from "@helpers/useCurrency";
import noLogoImage from "@assets/cur8/img/no-logo-deal.png";
import { assetClassBadge } from ".";
import { EntityBadge } from "@components/EntityBadge";
import { Commitment, Deal, Entity } from "@ifgengineering/client-invest-sdk";
import { PerformanceData } from "../PortfolioOverview/PortfolioOverview.component";

interface PortfolioInvestmentCardProps {
  commitment: Commitment;
  deal?: Deal;
  displayEntityBadge?: boolean;
  onClick?: () => void;
}

const PortfolioInvestmentCard = ({
  deal,
  commitment,
  displayEntityBadge = false,
  onClick,
}: PortfolioInvestmentCardProps) => {
  const assetClass = deal?.investable?.metadata?.assetClass;
  const currency = commitment?.currency || "GBP";
  const isIfIsa = !!commitment?.ifIsaAccountId;

  const theme = useTheme();
  const badge = assetClassBadge(theme, assetClass);
  const { toCurrency } = useCurrency(currency);
  const performanceData = useMemo(() => {
    return {
      value: (commitment as any)?.currentValue ?? 0,
      earnings: commitment?.profitLoss ?? 0,
      growth: commitment?.profitLossPercentage ?? 0,
    };
  }, [commitment]);

  const dealName = commitment?.dealName || "";
  const dealImageUrl = deal?.investable?.displayImage?.url;
  const dealLogoUrl = dealImageUrl || noLogoImage;

  return (
    <PortfolioInvestmentCardContainer onClick={onClick}>
      <LeftSectionContainer>
        <TopLeftSectionContainer>
          <HeadingWrapper>
            <CurrentValueContainer>
              <ThemedText type="S14" color="SLATE700">
                {dealName}
              </ThemedText>
            </CurrentValueContainer>
            <GrowthContainer>
              <ThemedText type="T18" color="SLATE900">
                {toCurrency(performanceData.value)}
              </ThemedText>
              <PerformanceData
                variant="small"
                currency={currency}
                growth={performanceData.growth}
                earnings={performanceData.earnings}
              />
            </GrowthContainer>
          </HeadingWrapper>
          <TagsWrapper>
            <AssetClassContainer background={badge.background}>
              <AssetClassText
                type="U12"
                color="SLATE800"
                textColor={badge.text}
              >
                {assetClass?.toUpperCase() || "Others"}
              </AssetClassText>
            </AssetClassContainer>
            {displayEntityBadge && (
              <EntityBadge
                entity={commitment?.entity as Entity}
                isIfIsa={isIfIsa}
                iconConfig={{
                  showLabel: true,
                  labelSize: "U12",
                  customBorderRadius: "8px",
                  customPadding: "4px 8px",
                }}
                displayNameConfig={{ hide: true }}
                hideInfo={true}
              />
            )}
          </TagsWrapper>
        </TopLeftSectionContainer>
      </LeftSectionContainer>
      <RightSectionContainer>
        <DealLogo
          src={dealLogoUrl}
          alt={dealName}
          $isFallback={!dealImageUrl}
        />
      </RightSectionContainer>
    </PortfolioInvestmentCardContainer>
  );
};

export default PortfolioInvestmentCard;
